// src/App.js
import { Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
import { Box } from "@mui/material";
import Navbar, { NAV_RESTING_HEIGHT } from "./components/Navbar/Navbar";
import TopCurve from "./components/TopCurve/TopCurve";
import ScrollToTop from "./components/ScrollToTop/ScrollToTop";
import RouteMeta from "./components/SEO/RouteMeta";
import AppBackground from "./components/AppBackground/AppBackground";
import { PageTransition } from "./components/Motion";
import { Home } from "./pages/Home/Home";
import { chunks, useReadyLocation } from "./routes/routeChunks";
import RoutePrefetch from "./routes/RoutePrefetch";
// Hidden for now - Component Design showcase page temporarily disabled (kept for later).
// import CompDesign from "./pages/ComponentsDesign/CompDesign";

// Every page but Home loads as its own chunk; see routes/routeChunks.jsx.
const Work = chunks.work.Component;
const CaseStudy = chunks.caseStudy.Component;
const LiveDemos = chunks.liveDemos.Component;
const About = chunks.about.Component;
const Resume = chunks.resume.Component;
const Contact = chunks.contact.Component;
const UxWriting = chunks.uxWriting.Component;
const WritingSamples = chunks.writingSamples.Component;

const AppShell = () => {
  // The page on screen changes only once the next page's code has loaded, so a
  // page transition never fades in an empty placeholder.
  const location = useReadyLocation(useLocation());

  return (
    <>
      <AppBackground />
      <ScrollToTop />
      <RouteMeta />
      <RoutePrefetch />
      <Box>
        <TopCurve />
        <Navbar />
        {/* The nav is fixed so it can never shift the page while scrolling; this
            reserves the height it used to occupy in the flow, so every page
            still starts exactly where it did. */}
        <Box aria-hidden="true" sx={{ height: NAV_RESTING_HEIGHT }} />
        {/* `location` is passed explicitly so the outgoing page keeps rendering
            its own route while it fades out. */}
        <PageTransition location={location}>
          {/* Only reached when a page's chunk is still loading on a first visit
              straight to it, which index.js normally rules out. */}
          <Suspense fallback={null}>
            <Routes location={location}>
              {/* Main Pages */}
              <Route path="/" element={<Home />} />
              <Route path="/work" element={<Work />} />
              <Route path="/work/:slug" element={<CaseStudy />} />
              {/* /live-demos, not /demos: /demos/* is where the built demo apps
                  are served from, and they carry a noindex header. */}
              <Route path="/live-demos" element={<LiveDemos />} />
              <Route path="/ux-writing" element={<UxWriting />} />
              <Route path="/writing-samples" element={<WritingSamples />} />
              <Route path="/about" element={<About />} />
              <Route path="/resume" element={<Resume />} />
              <Route path="/contact" element={<Contact />} />

              {/* Legacy/Utility Routes */}
              {/* Hidden for now - Component Design showcase temporarily disabled (kept for later). */}
              {/* <Route path="/compdesign" element={<CompDesign />} /> */}
            </Routes>
          </Suspense>
        </PageTransition>
      </Box>
    </>
  );
};

function App() {
  return (
    <Router>
      <AppShell />
    </Router>
  );
}

export default App;
